import { Bot, HelpCircle, User, Workflow } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CoverageDataSource,
  CoverageDataSourceInfo,
} from '@/services/types/coverage-types';
import { formatRelativeTime } from '@/utils/taskDateUtils';

interface DataSourceBadgeProps {
  source: CoverageDataSourceInfo | null | undefined;
  className?: string;
}

/**
 * Display a badge indicating the source of coverage data (BPO, User Edit, System, etc.)
 */
export const DataSourceBadge = ({
  source,
  className,
}: DataSourceBadgeProps) => {
  const getSourceDisplay = () => {
    if (!source) {
      return {
        label: 'Unknown',
        icon: HelpCircle,
        color: 'text-gray-400 bg-gray-50 border-gray-200',
        tooltip: 'Source unknown',
      };
    }

    switch (source.source) {
      case CoverageDataSource.BPO:
        return {
          label: 'BPO',
          icon: Bot,
          color: 'text-purple-600 bg-purple-50 border-purple-200',
          tooltip: 'Added by BPO agent',
        };
      case CoverageDataSource.USER_EDIT:
        return {
          label: 'Edited',
          icon: User,
          color: 'text-amber-600 bg-amber-50 border-amber-200',
          tooltip: source.user_name
            ? `Manually edited by ${source.user_name}`
            : 'Manually edited by user',
        };
      case CoverageDataSource.SYSTEM:
        return {
          label: 'System',
          icon: Workflow,
          color: 'text-gray-600 bg-gray-50 border-gray-200',
          tooltip: 'Added by system automation',
        };
      case CoverageDataSource.VOICE_AI:
        return {
          label: 'System',
          icon: Workflow,
          color: 'text-gray-600 bg-gray-50 border-gray-200',
          tooltip: 'Added by system automation',
        };
      case CoverageDataSource.UNKNOWN:
      default:
        return {
          label: 'Unknown',
          icon: HelpCircle,
          color: 'text-gray-400 bg-gray-50 border-gray-200',
          tooltip: 'Source unknown',
        };
    }
  };

  const { label, icon: Icon, color, tooltip } = getSourceDisplay();

  const formattedTime = source?.timestamp
    ? formatRelativeTime(source.timestamp)
    : null;

  const fullTooltip = formattedTime ? `${tooltip} • ${formattedTime}` : tooltip;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`text-xs font-normal gap-1 cursor-help ${color} ${className}`}
          >
            <Icon className="h-3 w-3" />
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{fullTooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
